import { useState, type ReactNode } from 'react';
import { Link } from 'react-router';
import { useMutation } from '@tanstack/react-query';
import { ArrowDown, ArrowUp, Beer, Car, CircleDot, Copy, Footprints, Mail, Map as MapIcon, Trash2, X } from 'lucide-react';
import { api, ApiError } from '../lib/api';
import { DAYS_LONG, hoursText, minutes, openOn, place, today } from '../lib/format';
import { isBar, kindLabel, SODA_NOTE, TRIP_KINDS, tripKindLabel } from '../lib/places';
import { tripLink } from '../lib/platform';
import { googleMapsTripUrl, mapEnds, tripLegs, waypointsFor, type Leg } from '../lib/tripEnds';
import { openExternal } from '../mobile/drive';
import { useTrip } from '../state/trip';
import PlaceMap from '../components/PlaceMap';
import TripEndRow from '../components/TripEndRow';
import { Empty, ErrorNote, Field, PageHeader } from '../components/ui';

export default function TripPage() {
  const { trip, update, remove, move, clear } = useTrip();
  const [confirmClear, setConfirmClear] = useState(false);
  const [shared, setShared] = useState<{ code: string; url: string } | null>(null);

  const tripDay = trip.date ? new Date(`${trip.date}T12:00:00`).getDay() : today();
  const legs = tripLegs(trip, trip.stops);
  const ends = mapEnds(trip);
  const gmaps = googleMapsTripUrl(trip, trip.stops);
  const closed = trip.stops.filter((s) => openOn(s, tripDay) === false);
  // Only a bar hop needs the driver and the pledge; any trip needs a stop (one is plenty).
  const barHop = trip.kind === 'BarHop';
  const driverReady = trip.driver.trim().length > 0 && trip.pledged;
  const ready = trip.stops.length > 0 && (!barHop || driverReady);

  const create = useMutation({
    mutationFn: () =>
      api.createTrip({
        name: trip.name.trim() || `Backroad ${tripKindLabel(trip.kind).toLowerCase()}`,
        kind: trip.kind,
        designatedDriver: barHop ? trip.driver.trim() : null,
        driverPledged: barHop ? trip.pledged : false,
        tripDate: trip.date || null,
        placeIds: trip.stops.map((s) => s.id),
        waypoints: waypointsFor(trip, trip.stops),
      }),
    onSuccess: setShared,
  });

  return (
    <>
      <PageHeader eyebrow="Saved on this device" title="My trip">
        Pick the kind of trip, set where you start and finish, put the stops in order, then share it. A bar hop also
        names its designated driver.
      </PageHeader>

      <div className="mx-auto max-w-7xl px-4 pb-14 sm:px-6">
        <div className="grid gap-6 lg:grid-cols-[1fr_380px]">
          <div className="flex min-w-0 flex-col gap-4">
            <div className="card grid grid-cols-3 gap-3 p-5">
              <Total value={String(trip.stops.length)} label="Stops" />
              <Total value={`${Math.round(legs.driveMiles)}`} label="Est. miles" />
              <Total value={minutes(legs.driveMinutes)} label="Est. driving" />
            </div>

            {closed.length > 0 && (
              <ErrorNote>
                Closed {DAYS_LONG[tripDay]}: {closed.map((c) => c.name).join(', ')}. Swap them out or pick another day.
              </ErrorNote>
            )}

            {trip.stops.length > 0 && (
              <PlaceMap
                stops={trip.stops}
                connectStops
                routeFrom={ends.routeFrom}
                routeTo={ends.routeTo}
                waypoints={ends.waypoints}
                fitPoints={[...trip.stops.map((s) => [s.lat, s.lng] as [number, number]), ...ends.fit]}
                fitKey={`${trip.stops.map((s) => s.id).join(',')}|${ends.fit.join(';')}`}
                className="h-[380px]"
                label="Map of my trip"
              />
            )}

            <ol className="flex flex-col">
              <li>
                <TripEndRow which="start" />
                <LegLine leg={legs.first} />
              </li>
              {trip.stops.length === 0 && (
                <li className="py-2">
                  <Empty>
                    No stops yet. <Link to="/plan">Plan a trip</Link>, pick a <Link to="/routes">route</Link>, or{' '}
                    <Link to="/explore">explore the map</Link> and tap “Add to trip”.
                  </Empty>
                </li>
              )}
              {trip.stops.map((s, i) => {
                const open = openOn(s, tripDay);
                const leg = i < trip.stops.length - 1 ? legs.between[i] : legs.last;
                return (
                  <li key={s.id}>
                    <div className="card flex items-center gap-3 p-3">
                      <span className="flex size-8 flex-none items-center justify-center rounded-full bg-amber font-extrabold text-amber-ink">{i + 1}</span>
                      <div className="flex min-w-0 flex-1 flex-col">
                        <Link to={`/stops/${s.slug}`} className="truncate font-bold text-cream hover:text-amber-hi">
                          {s.name}
                        </Link>
                        <span className={`text-sm ${open === false ? 'text-warn' : 'text-muted'}`}>
                          {[kindLabel(s.kind), place(s), hoursText(s, tripDay)].filter(Boolean).join(' · ')}
                        </span>
                        {!barHop && isBar(s.kind) && <span className="text-[13px] text-dd">{SODA_NOTE}</span>}
                      </div>
                      <div className="flex gap-1">
                        <IconButton label={`Move ${s.name} up`} disabled={i === 0} onClick={() => move(i, -1)}>
                          <ArrowUp size={18} aria-hidden />
                        </IconButton>
                        <IconButton label={`Move ${s.name} down`} disabled={i === trip.stops.length - 1} onClick={() => move(i, 1)}>
                          <ArrowDown size={18} aria-hidden />
                        </IconButton>
                        <IconButton label={`Remove ${s.name}`} onClick={() => remove(s.id)}>
                          <X size={18} aria-hidden />
                        </IconButton>
                      </div>
                    </div>
                    <LegLine leg={leg} />
                  </li>
                );
              })}
              <li className={legs.last || trip.stops.length === 0 ? '' : 'pt-2'}>
                <TripEndRow which="end" />
              </li>
            </ol>

            {trip.stops.length > 0 && (
              <>
                {gmaps && (
                  <div className="flex flex-col gap-1">
                    <button type="button" className="btn self-start" onClick={() => void openExternal(gmaps.url)}>
                      <MapIcon size={18} aria-hidden /> Open the whole trip in Google Maps
                    </button>
                    {gmaps.stopsIncluded < trip.stops.length && (
                      <span className="text-[13px] text-dim">
                        Google Maps takes {gmaps.stopsIncluded} stops at a time, so the link stops at {trip.stops[gmaps.stopsIncluded - 1].name}.
                      </span>
                    )}
                  </div>
                )}
                <div className="flex flex-wrap gap-2">
                  {!confirmClear ? (
                    <button type="button" className="btn btn-sm" onClick={() => setConfirmClear(true)}>
                      <Trash2 size={16} aria-hidden /> Clear trip
                    </button>
                  ) : (
                    <div className="flex flex-wrap items-center gap-2 rounded-xl bg-warn-bg px-3 py-2 text-sm text-warn">
                      Clear every stop?
                      <button type="button" className="btn btn-sm btn-primary" onClick={() => { clear(); setConfirmClear(false); setShared(null); }}>
                        Clear it
                      </button>
                      <button type="button" className="btn btn-sm" onClick={() => setConfirmClear(false)}>
                        Keep it
                      </button>
                    </div>
                  )}
                </div>
                <p className="text-[13px] text-dim">Drive times are rough estimates. Use your phone&apos;s maps for turn-by-turn.</p>
              </>
            )}
          </div>

          <aside className="flex flex-col gap-4">
            <TripKindPicker />

            <div className="card flex flex-col gap-4 p-5">
              <Field label="Trip name" htmlFor="tripName">
                <input id="tripName" type="text" placeholder="Saturday river run" value={trip.name} onChange={(e) => update({ name: e.target.value })} maxLength={120} />
              </Field>
              <Field label="Date" htmlFor="tripDate" hint="Used to check which stops are open.">
                <input id="tripDate" type="date" value={trip.date} onChange={(e) => update({ date: e.target.value })} />
              </Field>
            </div>

            {barHop && (
              <div className="flex flex-col gap-3 rounded-2xl border border-dd-line bg-dd-bg p-5">
                <span className="text-[13px] font-bold uppercase tracking-[0.1em] text-dd">Who&apos;s driving?</span>
                <p className="text-[15px] text-[#d6d2c2]">Every bar hop needs a designated driver who stays sober the whole time.</p>
                <Field label="Designated driver" htmlFor="driver">
                  <input id="driver" type="text" placeholder="Name" value={trip.driver} onChange={(e) => update({ driver: e.target.value })} maxLength={80} />
                </Field>
                <label className="flex items-start gap-3 font-normal">
                  <input type="checkbox" className="mt-1 size-5 accent-[#6CC5B0]" checked={trip.pledged} onChange={(e) => update({ pledged: e.target.checked })} />
                  <span>
                    {trip.driver.trim() || 'Our driver'} pledges to stay sober and get everyone home safe.
                  </span>
                </label>
                <span className={`chip self-start ${ready ? 'chip-ok' : 'chip-warn'}`}>
                  {ready ? 'Bar hop is ready' : trip.stops.length === 0 ? 'Add a stop first' : 'Name a driver and take the pledge'}
                </span>
              </div>
            )}

            <div className="card flex flex-col gap-3 p-5">
              <h2 className="text-2xl font-black">Share with the group</h2>
              {!shared ? (
                <>
                  {!barHop && (
                    <span className={`chip self-start ${ready ? 'chip-ok' : 'chip-warn'}`}>{ready ? 'Trip is ready' : 'Add a stop first'}</span>
                  )}
                  <button type="button" className="btn btn-primary" disabled={!ready || create.isPending} onClick={() => create.mutate()}>
                    {create.isPending ? 'Saving…' : 'Save & get a link'}
                  </button>
                  {create.error && <ErrorNote error={create.error} />}
                </>
              ) : (
                <SharePanel code={shared.code} url={shared.url} onReset={() => setShared(null)} />
              )}
            </div>
          </aside>
        </div>
      </div>
    </>
  );
}

const KIND_ICONS = { RoadTrip: Car, DayOut: CircleDot, BarHop: Beer } as const;

/** Road trip, day out or bar hop. Only a bar hop asks for a designated driver. */
function TripKindPicker() {
  const { trip, update } = useTrip();
  return (
    <div className="card flex flex-col gap-3 p-5">
      <h2 id="tripKindH" className="text-2xl font-black">
        What kind of trip?
      </h2>
      <div className="grid gap-2" role="radiogroup" aria-labelledby="tripKindH">
        {TRIP_KINDS.map((k) => {
          const on = trip.kind === k.value;
          const dd = k.value === 'BarHop';
          const Icon = KIND_ICONS[k.value];
          return (
            <button
              key={k.value}
              type="button"
              role="radio"
              aria-checked={on}
              onClick={() => update({ kind: k.value })}
              className={`flex items-center gap-3 rounded-xl border px-3 py-2.5 text-left transition-colors ${
                on ? (dd ? 'border-dd bg-dd-bg' : 'border-amber bg-panel-2') : 'border-line-2 hover:border-muted'
              }`}
            >
              <span
                className={`flex size-9 flex-none items-center justify-center rounded-full ${
                  on ? (dd ? 'bg-dd text-asphalt' : 'bg-amber text-amber-ink') : 'bg-panel-2 text-cream'
                }`}
              >
                <Icon size={18} aria-hidden />
              </span>
              <span className="flex min-w-0 flex-col">
                <b className={on ? (dd ? 'text-dd' : 'text-amber-hi') : 'text-cream'}>{k.label}</b>
                <span className="text-[13px] text-muted">{k.hint}</span>
              </span>
            </button>
          );
        })}
      </div>
    </div>
  );
}

function SharePanel({ code, url, onReset }: { code: string; url: string; onReset: () => void }) {
  const link = tripLink(code, url);
  const [copied, setCopied] = useState(false);
  const [sender, setSender] = useState('');
  const [emails, setEmails] = useState('');
  const [message, setMessage] = useState('');

  const send = useMutation({
    mutationFn: () =>
      api.shareTrip(code, {
        senderName: sender.trim(),
        recipients: emails.split(/[\s,;]+/).map((e) => e.trim()).filter(Boolean),
        message: message.trim() || undefined,
      }),
  });
  const fieldErrors = send.error instanceof ApiError ? send.error.fieldErrors : {};

  return (
    <div className="flex flex-col gap-3">
      <div className="flex gap-2">
        <input readOnly value={link} aria-label="Trip link" onFocus={(e) => e.target.select()} />
        <button
          type="button"
          className="btn w-11 flex-none px-0"
          aria-label="Copy link"
          onClick={async () => {
            try {
              await navigator.clipboard.writeText(link);
              setCopied(true);
              window.setTimeout(() => setCopied(false), 2000);
            } catch {
              /* clipboard blocked; the field is selectable */
            }
          }}
        >
          <Copy size={18} aria-hidden />
        </button>
      </div>
      {copied && <span className="text-sm text-ok" role="status">Copied. Paste it in the group chat.</span>}
      <Link to={`/trip/${code}`} className="text-sm">
        Open the shared trip page
      </Link>
      <span className="sr-only">{url}</span>

      <form
        className="mt-2 flex flex-col gap-3 border-t border-line pt-4"
        onSubmit={(e) => {
          e.preventDefault();
          send.mutate();
        }}
      >
        <span className="flex items-center gap-2 font-semibold">
          <Mail size={16} aria-hidden /> Email it
        </span>
        <Field label="Your name" htmlFor="sender" error={fieldErrors.senderName}>
          <input id="sender" type="text" value={sender} onChange={(e) => setSender(e.target.value)} required maxLength={80} />
        </Field>
        <Field label="Emails" htmlFor="emails" hint="Separate with commas or new lines. Up to 10." error={fieldErrors.recipients ?? Object.entries(fieldErrors).filter(([k]) => k.startsWith('recipients[')).flatMap(([, v]) => v)}>
          <textarea id="emails" value={emails} onChange={(e) => setEmails(e.target.value)} required />
        </Field>
        <Field label="Message (optional)" htmlFor="message" error={fieldErrors.message}>
          <input id="message" type="text" value={message} onChange={(e) => setMessage(e.target.value)} maxLength={500} placeholder="Wheels up at noon!" />
        </Field>
        <button type="submit" className="btn btn-primary" disabled={send.isPending}>
          {send.isPending ? 'Sending…' : 'Send to the group'}
        </button>
        {send.data && <span className="chip chip-ok self-start" role="status">Sent to {send.data.sent} {send.data.sent === 1 ? 'person' : 'people'}</span>}
        {send.error && !Object.keys(fieldErrors).length && <ErrorNote error={send.error} />}
      </form>

      <button type="button" className="btn btn-sm self-start" onClick={onReset}>
        Changed the trip? Save a new link
      </button>
    </div>
  );
}

function LegLine({ leg }: { leg: Leg | null }) {
  if (!leg) return null;
  return (
    <div className="flex items-center gap-2 py-1.5 pl-7 text-sm tabular-nums text-muted">
      {leg.walk ? <Footprints size={14} aria-hidden /> : <span className="h-4 border-l-2 border-dashed border-amber" aria-hidden />}
      {leg.walk ? `Walk · ~${leg.miles.toFixed(1)} mi` : `~${Math.round(leg.miles)} mi · ~${leg.minutes} min drive`}
    </div>
  );
}

function Total({ value, label }: { value: string; label: string }) {
  return (
    <div className="flex flex-col">
      <b className="font-display text-4xl leading-none tabular-nums">{value}</b>
      <small className="text-xs uppercase tracking-[0.08em] text-muted">{label}</small>
    </div>
  );
}

function IconButton({ label, disabled, onClick, children }: { label: string; disabled?: boolean; onClick: () => void; children: ReactNode }) {
  return (
    <button type="button" aria-label={label} disabled={disabled} onClick={onClick} className="inline-flex size-10 items-center justify-center rounded-lg border border-line-2 text-cream disabled:opacity-35">
      {children}
    </button>
  );
}
